/**
 * CoverageCore — the LOD coverage DECISION layer, extracted pure.
 *
 * VoxelWorld owns I/O (chunks maps, BFS runs, meshes, network); this module owns the VERDICTS,
 * expressed over an injected read interface so the coverage harness
 * (`testkit/coverageHarness.ts`) can drive the very same code headlessly — the physics
 * `stepPlayer` pattern applied to streaming. Nothing in here may touch THREE, workers, timers,
 * or maps it does not own.
 *
 * Extraction is happening in slices, each behaviour-neutral and parity-gated by the harness
 * checksums; this file grows as VoxelWorld shrinks. Slice 1: base-chunk classification.
 */

export type BaseChunkState = 'sky' | 'outside' | 'occluded' | 'drawable' | 'waiting';

/** The facts the classification needs — implemented by VoxelWorld live, by fixtures in tests. */
export interface CoverageReads {
  /** Genuine open sky above the column's content top (no data exists or ever will). */
  isEmptyAir(cx: number, cy: number, cz: number): boolean;
  /** The BFS's octet-aligned traversal box, or null before any BFS has run. */
  bfsBox(): { minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number } | null;
  /** In the render set (reachable ∪ face-ring) — VoxelWorld.shouldMeshChunk at lod 0. */
  inRenderSet(cx: number, cy: number, cz: number): boolean;
  /** The last BFS met unloaded terrain inside its box (VisibilityResult.frontierActive). */
  frontierActive(): boolean;
  /** Chunk resident with its mesh applied complete (ChunkPhase.MeshedComplete) at lod 0. */
  isMeshedComplete(cx: number, cy: number, cz: number): boolean;
}

/** Memo codes, in the order `STATE_BY_CODE` decodes them. 0 means "not yet decided this pass". */
const CODE_SKY = 1, CODE_OUTSIDE = 2, CODE_OCCLUDED = 3, CODE_DRAWABLE = 4, CODE_WAITING = 5;
const STATE_BY_CODE: readonly BaseChunkState[] = ['sky', 'outside', 'occluded', 'drawable', 'waiting'];

export class CoverageCore {
  constructor(private readonly reads: CoverageReads) {}

  /**
   * PER-PASS MEMO over the BFS box.
   *
   * A base chunk's classification is asked TWICE per pass for most positions: once by the LOD walk
   * (through isBaseCovered) and once by the render gate as it walks the geometry map. Each answer
   * costs up to ten probes — the column tile, the box test, the render-set test's own seven, and the
   * mesh phase — so the second ask was pure duplication of the first.
   *
   * Scoped EXPLICITLY to a pass rather than kept live, because baseChunkState is also read by the
   * dev audit at frame end, and on a frame where no pass ran a live memo would hand it verdicts from
   * an older one. Outside a pass there is no memo and every call classifies fresh.
   *
   * Dense over the box, because that is the region both callers overlap in and because a packed-coord
   * key would be far outside V8's small-integer range. Positions outside the box are not memoized:
   * their answer is one `isEmptyAir` probe away and they are the minority (the retained hysteresis
   * ring), so they cost a probe rather than a grid the size of the retention radius.
   */
  private memo: Int8Array = new Int8Array(0);
  private memoBox: {
    minX: number; minY: number; minZ: number; sx: number; sy: number; sz: number;
  } | null = null;

  /** Open the memo for one visibility pass. */
  beginPass(): void {
    const box = this.reads.bfsBox();
    this.memoBox = null;
    if (!box) return;
    const sx = box.maxX - box.minX + 1;
    const sy = box.maxY - box.minY + 1;
    const sz = box.maxZ - box.minZ + 1;
    const cells = sx * sy * sz;
    if (sx <= 0 || sy <= 0 || sz <= 0) return;
    if (this.memo.length < cells) this.memo = new Int8Array(cells);
    this.memo.fill(0, 0, cells);
    this.memoBox = { minX: box.minX, minY: box.minY, minZ: box.minZ, sx, sy, sz };
  }

  /** Close it — anything asking after this classifies fresh. */
  endPass(): void {
    this.memoBox = null;
  }

  private memoSlot(cx: number, cy: number, cz: number): number {
    const b = this.memoBox;
    if (b === null) return -1;
    const ix = cx - b.minX, iy = cy - b.minY, iz = cz - b.minZ;
    if (ix < 0 || iy < 0 || iz < 0 || ix >= b.sx || iy >= b.sy || iz >= b.sz) return -1;
    return (iz * b.sy + iy) * b.sx + ix;
  }

  /** The classification as a memo code — the one place the terms are evaluated. */
  private classifyCode(cx: number, cy: number, cz: number): number {
    if (this.reads.isEmptyAir(cx, cy, cz)) return CODE_SKY;
    const box = this.reads.bfsBox();
    if (!box || cx < box.minX || cx > box.maxX || cy < box.minY || cy > box.maxY
      || cz < box.minZ || cz > box.maxZ) {
      return CODE_OUTSIDE;
    }
    if (!this.reads.inRenderSet(cx, cy, cz)) {
      return this.reads.frontierActive() ? CODE_WAITING : CODE_OCCLUDED;
    }
    return this.reads.isMeshedComplete(cx, cy, cz) ? CODE_DRAWABLE : CODE_WAITING;
  }

  private codeAt(cx: number, cy: number, cz: number): number {
    const slot = this.memoSlot(cx, cy, cz);
    if (slot >= 0) {
      const m = this.memo[slot];
      if (m !== 0) return m;
    }
    const code = this.classifyCode(cx, cy, cz);
    if (slot >= 0) this.memo[slot] = code;
    return code;
  }

  /**
   * SINGLE SOURCE OF TRUTH for what a base (lod 0) chunk contributes on screen. Both the LOD
   * coverage predicate (isBaseCovered) and the render gate derive from THIS function — they must
   * never re-implement these terms independently: the two drifting apart is exactly how the
   * swap-gap and surface-hole regressions happened (coverage said a chunk's volume was
   * represented while the gate refused to draw it).
   *
   *  - 'sky'      provable open air above the column top; nothing exists to draw.
   *  - 'outside'  beyond the BFS aligned box (or pre-BFS): distance-transitional, never covered;
   *               drawn only as a selected fallback when complete.
   *  - 'occluded' in the box but not in the render set (reachable ∪ face-ring): unseeable.
   *               ONLY trusted once the BFS ran with no streaming frontier inside its box.
   *               While the frontier is active, "unreached" mostly means "behind rock that
   *               hasn't streamed yet" — indistinguishable, cell by cell, from true occlusion —
   *               so those cells classify as 'waiting' and parents keep covering (fix F1 for
   *               bug M1: counting them covered is what flickered lod0<->lod1 on initial load,
   *               the classification flipping under the walk as the frontier advanced).
   *  - 'drawable' in the box, wanted, MeshedComplete — the gate WILL draw it when selected.
   *  - 'waiting'  in the box, wanted, but not yet drawable (unloaded / queued / meshing /
   *               incomplete): its parent must keep covering.
   */
  baseChunkState(cx: number, cy: number, cz: number): BaseChunkState {
    return STATE_BY_CODE[this.codeAt(cx, cy, cz) - 1];
  }

  /**
   * Base-level external coverage for the LOD residency walk: is this base chunk's volume fully
   * represented WITHOUT a mesh of its own?
   *
   *  - Provable open sky (above the column's content top) → covered.
   *  - OCCLUSION-pruned (the BFS evaluated its cell and could not see it through the visibility
   *    graph) → covered: the volume is unseeable, so a parent may swap without it.
   *  - DISTANCE-culled is NOT coverage. The base in-range set is octet-rounded (and larger than
   *    the BFS box, so every drawable chunk is managed), which puts chunks BEYOND the BFS cut in
   *    range. Those are perfectly visible — they're just not drawn at fine LOD — so their parent
   *    must stay drawn. This includes MESHED outside-box chunks: counting them covered released
   *    ring-1 parents past the frontier, and their later unload flashed a hole.
   *  - "Won't draw" is NEVER coverage. Two past regressions came from classifying stuck chunks
   *    (unloaded-behind-request-budgets; MeshedIncomplete with unrequested margins) as covered so
   *    their parent could release: budget-queued is not never-coming, and releasing a parent over
   *    an undrawable SURFACE chunk is a visible hole. The cure for an undrawable chunk is to make
   *    it drawable — healIncompleteMargins requests the missing margins — while the parent keeps
   *    covering ('waiting' is not covered).
   *  - NOT "fully underground": caves can make underground base chunks visible.
   */
  isBaseCovered(cx: number, cy: number, cz: number): boolean {
    // On the code rather than the string: this runs once per base node of the walk.
    const code = this.codeAt(cx, cy, cz);
    return code === CODE_SKY || code === CODE_OCCLUDED || code === CODE_DRAWABLE;
  }
}
